import { Box, Button, Group, Stack, Text } from '@mantine/core';
import { IconCancel as CancelIcon, IconDeviceFloppy as SaveIcon } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { FormEventHandler, PropsWithChildren, ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

// The submitting prop only disables the button a render later, and goes back to false while the navigation after a save still loads.
function useSingleSubmit(onSubmit: FormEventHandler<HTMLFormElement>): FormEventHandler<HTMLFormElement> {
  const queryClient = useQueryClient();
  // Without a router there is no navigation to wait for.
  const router = useRouter({ warn: false }) as ReturnType<typeof useRouter> | null;
  const lockedRef = useRef(false);
  const stopWaitingRef = useRef<(() => void) | null>(null);

  useEffect(() => () => stopWaitingRef.current?.(), []);

  return event => {
    if (lockedRef.current) {
      event.preventDefault();
      return;
    }
    lockedRef.current = true;

    const mutationCache = queryClient.getMutationCache();
    const known = new Set(mutationCache.getAll());
    const stopWaiting = () => {
      stopWaitingRef.current?.();
      stopWaitingRef.current = null;
    };
    const release = () => {
      stopWaiting();
      lockedRef.current = false;
    };
    const waitForStartedMutations = () => {
      const started = mutationCache.getAll().filter(mutation => !known.has(mutation));
      const settle = () => {
        if (started.some(mutation => mutation.state.status === 'pending')) {
          return false;
        }
        const saved = started.some(mutation => mutation.state.status === 'success');
        if (saved && router?.state.isLoading) {
          stopWaiting();
          stopWaitingRef.current = router.subscribe('onResolved', release);
        } else {
          release();
        }
        return true;
      };

      if (!settle()) {
        stopWaitingRef.current = mutationCache.subscribe(() => {
          settle();
        });
      }
    };

    let submitted: unknown;
    try {
      submitted = onSubmit(event);
    } catch (error) {
      release();
      throw error;
    }

    Promise.resolve(submitted)
      .then(waitForStartedMutations)
      .catch((error: unknown) => {
        release();
        throw error;
      });
  };
}

export interface FormProps {
  onSubmit: FormEventHandler<HTMLFormElement>;
  onCancel?: () => void;
  title?: string;

  isSubmitting: boolean;
  isDisabled?: boolean;
  withoutSubmit?: boolean;

  submitLabel?: string;
  submitColor?: string;
  submitIcon?: ReactNode;
}

export function Form(props: PropsWithChildren<FormProps>) {
  const {
    children,
    title,
    onSubmit,
    onCancel,
    isSubmitting,
    isDisabled,
    withoutSubmit,
    submitLabel,
    submitColor,
    submitIcon,
  } = props;
  const { t } = useTranslation();
  const handleSubmit = useSingleSubmit(onSubmit);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      p="xl"
      style={{ '--radius': '0.5rem', borderRadius: 'var(--radius)' }}
    >
      {title && (
        <Text
          component="h1"
          size="lg"
          mb="sm"
          fw="500"
        >
          {title}
        </Text>
      )}
      <Stack>{children}</Stack>
      <Group
        mt="lg"
        justify="flex-end"
      >
        {onCancel && (
          <Button
            leftSection={<CancelIcon />}
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {t('button.cancel')}
          </Button>
        )}
        {!withoutSubmit && (
          <Button
            leftSection={submitIcon ?? <SaveIcon />}
            color={submitColor}
            type="submit"
            disabled={isSubmitting || isDisabled}
            loading={isSubmitting}
          >
            {submitLabel ?? t('button.save')}
          </Button>
        )}
      </Group>
    </Box>
  );
}
